import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import {
  ArrowRight, Shield, Brain, Heart, Zap, Moon, Activity,
  CheckCircle2, Smartphone, BarChart3, Bell, Flame, Award,
  Pill, Mail, Star, ChevronRight, Eye, Leaf
} from 'lucide-react';
import { Button } from '../components/ui/button';
import GlobalFooter from '../components/GlobalFooter';
import AppStoreBadges from '../components/AppStoreBadges';

const features = [
  {
    icon: Brain,
    title: 'AI-Powered Recommendations',
    desc: 'Our clinical AI analyzes 34 health dimensions to create a supplement plan unique to your body.'
  },
  {
    icon: Shield,
    title: 'Medication Safety Checks',
    desc: 'Every recommendation is cross-referenced against your medications for potential interactions.'
  },
  {
    icon: BarChart3,
    title: 'Wellness Journal & Trends',
    desc: 'Track energy, sleep, mood, digestion, and focus daily. See 7/30/90-day trends and correlation insights.'
  },
  {
    icon: Flame,
    title: 'Daily Check-ins & Streaks',
    desc: 'Build consistent habits with one-tap check-ins, streak tracking, and milestone celebrations.'
  },
  {
    icon: Pill,
    title: 'My Stack Tracker',
    desc: 'Track your active supplements with daily schedules, dosage reminders, and restock alerts.'
  },
  {
    icon: Award,
    title: '16 Achievement Milestones',
    desc: 'Unlock milestones as you progress — from First Steps to Wellness Champion at 90 days.'
  },
  {
    icon: Bell,
    title: 'Smart Reminders',
    desc: 'Configurable push notifications so you never miss a dose. Weekly wellness reports via email.'
  },
  {
    icon: Eye,
    title: 'Condition-Specific Guides',
    desc: 'Specialized protocols for MS, Thyroid, PCOS, Arthritis, Diabetes, and IBS.'
  },
];

const steps = [
  { num: '1', title: 'Take the Assessment', desc: 'Answer 34 health questions about your lifestyle, conditions, and goals.' },
  { num: '2', title: 'Get Your Plan', desc: 'Receive AI-generated supplement recommendations with dosages, timing, and safety checks.' },
  { num: '3', title: 'Track & Improve', desc: 'Log daily wellness, track your supplements, and watch your health improve over time.' },
];

export default function MarketingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border/50 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Logo size="small" linkTo="/" />
          <Link to="/quiz">
            <Button className="btn-primary rounded-full px-5 text-sm">
              Get Started Free <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
                <Leaf className="w-3.5 h-3.5" /> Available on iOS & Android
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1A1A] leading-tight mb-5">
                Smart Supplements.{' '}
                <span className="text-primary">Zero Guesswork.</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed max-w-lg">
                Vitala uses clinical AI to analyze your unique health profile and recommend exactly the supplements your body needs — with medication safety checks built in.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/quiz">
                  <Button size="lg" className="btn-primary rounded-full px-8 text-base w-full sm:w-auto">
                    Take Free Assessment <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/supplements">
                  <Button variant="outline" size="lg" className="rounded-full px-8 text-base w-full sm:w-auto">
                    Explore Supplement Guide
                  </Button>
                </Link>
              </div>
              <div className="mt-6">
                <p className="text-xs font-medium text-muted-foreground mb-3" data-testid="marketing-download-label">
                  Download the Vitala app
                </p>
                <AppStoreBadges variant="both" size="md" align="start" />
              </div>
              <div className="flex items-center gap-6 mt-8">
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground ml-1">5,000+ plans created</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1681264368290-13171e3bb4d9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=700"
                alt="Person using Vitala health app on phone"
                className="rounded-2xl shadow-2xl w-full object-cover"
                style={{ maxHeight: '480px' }}
              />
              {/* Floating stat card */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-border/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A1A]">100% Free</p>
                    <p className="text-xs text-muted-foreground">No hidden paywalls</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white border-y border-border/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">How Vitala Works</h2>
            <p className="text-muted-foreground max-w-md mx-auto">Three simple steps to a personalized supplement plan</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-primary">{step.num}</span>
                </div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">Everything You Need</h2>
          <p className="text-muted-foreground max-w-md mx-auto">A complete wellness companion in your pocket</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-border/50 p-5 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-sm text-[#1A1A1A] mb-1.5">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* App Screenshots */}
      <section className="bg-white border-y border-border/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">See It in Action</h2>
            <p className="text-muted-foreground max-w-md mx-auto">From assessment to daily tracking — your complete wellness journey</p>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
            {[
              { src: '/social/vitala-logo-white-bg.png', label: 'Your Brand' },
            ].map((item, i) => (
              <div key={i} className="flex-shrink-0 snap-center">
                <div className="w-48 sm:w-56 rounded-2xl overflow-hidden border border-border/50 shadow-sm">
                  <img src={item.src} alt={item.label} className="w-full" loading="lazy" />
                </div>
                <p className="text-xs text-muted-foreground text-center mt-2">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-5">Built on Trust</h2>
            <div className="space-y-4">
              {[
                { title: '100% Independent', desc: 'No affiliate links. No sponsored brands. No hidden partnerships. Our recommendations are purely evidence-based.' },
                { title: 'Medication Safety', desc: 'Every supplement is checked against your medications for interactions before being recommended.' },
                { title: 'Privacy First', desc: 'Your health data is encrypted and never shared with third parties. Delete anytime.' },
                { title: 'Free Forever Core', desc: 'Take the assessment, get your plan, track your supplements — all completely free.' },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm text-[#1A1A1A]">{item.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1647964186028-73747bdbb4a0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=600"
              alt="Health and wellness"
              className="rounded-2xl shadow-lg w-full object-cover"
              style={{ maxHeight: '400px' }}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-[#4A6C58] to-[#3A5A48]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Discover What Your Body Actually Needs
            </h2>
            <p className="text-white/70 max-w-lg mx-auto mb-8">
              Join thousands who've replaced guesswork with personalized, AI-powered supplement guidance.
            </p>
            <Link to="/quiz">
              <Button size="lg" className="bg-white text-[#4A6C58] hover:bg-white/90 rounded-full px-10 text-base font-semibold">
                Start Free Assessment <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact / Footer */}
      <section className="bg-white border-t border-border/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Logo size="small" linkTo="/" />
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            Personalized supplement guidance powered by clinical AI
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            Questions? Reach us at{' '}
            <a href="mailto:marketing@vitala.me" className="text-primary font-medium hover:underline">
              marketing@vitala.me
            </a>
          </p>
          <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-[#1A1A1A] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#1A1A1A] transition-colors">Terms of Service</Link>
            <Link to="/about" className="hover:text-[#1A1A1A] transition-colors">About</Link>
            <Link to="/faq" className="hover:text-[#1A1A1A] transition-colors">FAQ</Link>
          </div>
          <p className="text-xs text-muted-foreground mt-6">
            &copy; {new Date().getFullYear()} Vitala. All rights reserved.
          </p>
        </div>
      </section>
    </div>
  );
}
